import { useEffect, useState } from 'react';
import { apiUrl } from '../../../api/apiUrl';
import http from '../../../api/axios';
import DataTable from '../../../components/table/DataTable';
import { toast } from 'react-toastify';
import { useCurrentCompany } from '../../../hooks/useCompany';
import { columns } from '../../../components/columns/Company/FilesColumns';
import { onDownloadItem } from '../../../utils/functions/file.functions';
import { FileUpload } from '../../../components/files/FileUpload';
import { Loader } from '../../../components/loader/Loader';
import { AttachmentType, FileBase } from '../../../types/Files';
import { useTranslation } from '../../../context/translationContext';
import { TableFilter } from '../../../components/fIlter/TableFilter';
import { convertEnumToArray, supportedFileType } from '../../../utils/functions/data.functions';
import { FilterItemType } from '../../../components/fIlter/TableFilter.types';
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs';
import { paths } from '../../RouterContainer';
import { useLoading } from '../../../context/loadingContext';
import { Modal } from '../../../components/modal/modal';
import FilePreview from '../../../components/filePreview/FilePreview';

export interface FilesPageFilter {
  attachmentType?: AttachmentType | null;
}

/**
 * Page component represents list of files and upload interface
 */
export const FilesPage = () => {
  const { company } = useCurrentCompany();
  const { t } = useTranslation();
  const [files, setFiles] = useState<FileBase[] | null>(null);
  const [filter, setFilter] = useState<FilesPageFilter>();
  const { setBreadcrumbs } = useBreadcrumbs();
  const { startLoading, stopLoading } = useLoading();
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string>();

  useEffect(() => {
    setBreadcrumbs([{ path: `${paths.FILES}`, name: 'navigation.files' }]);
  }, []);

  /**
   * Loads files for current company
   */
  const fetchFiles = async () => {
    if (!company || !filter) return;
    let req = `?companyId=${company.id}`;
    if (filter.attachmentType) req += `&attachmentType=${filter.attachmentType}`;
    const response = await http.apiGet({
      url: `${apiUrl.FILE_LIST}${req}`,
    });
    setFiles(response?.data);
  };

  useEffect(() => {
    if (!company) return;
    fetchFiles();
  }, [filter]);

  useEffect(() => {
    if (!company || !company.id) return;
    setFilter({ attachmentType: undefined });
    fetchFiles();
  }, [company]);

  /**
   * Removes file
   * @param id of entity
   */
  const onRemoveItem = async (id: string | number) => {
    if (id === null) return;
    const response = await http.apiDelete({
      url: `${apiUrl.FILE_DELETE}?id=${id}`,
    });
    if (response?.status === 204) {
      toast.success('Success!');
      fetchFiles();
    }
  };

  /**
   * Opens preview of file
   */
  const handleOpenPreview = (id: string) => {
    setSelectedId(id);
    setPreviewOpen(true);
  };

  if (!company) return <Loader />;
  return (
    <>
      <div className="flex justify-between">
        <h1 className="text-3xl font-bold">{t('page.file.headline')}</h1>
        <div className="w-2/3">
          <FileUpload
            apiPostFilesUrl={`${apiUrl.FILE_UPLOAD}?companyId=${company.id}&attachmentType=${AttachmentType.OTHER}`}
            onUpload={() => fetchFiles()}
          />
        </div>
      </div>
      <TableFilter
        filter={filter}
        setFilter={setFilter}
        filterOptions={[
          {
            data: [
              { value: undefined, name: '-- select attachment type --' },
              ...convertEnumToArray(AttachmentType),
            ],
            type: FilterItemType.SELECT,
            valueKey: 'attachmentType',
          },
        ]}
      />
      <DataTable
        data={files}
        columns={columns}
        handleRemove={onRemoveItem}
        customButtons={[
          {
            title: t('general.preview'),
            color: 'blue',
            onSubmit: (e) => handleOpenPreview(e.toString()),
            renderCondition: (e) => {
              const contentType = files?.find((f: FileBase) => f.id === e)?.contentType;
              if (contentType) return supportedFileType(contentType);
              return false;
            },
          },
          {
            title: 'Download',
            color: 'blue',
            showLoading: true,
            onSubmit: (e) => {
              startLoading();
              onDownloadItem(
                `${apiUrl.FILE_DOWNLOAD}?id=${e}`,
                files?.find((f: FileBase) => f.id === e)?.name,
              ).then(() => stopLoading());
            },
          },
        ]}
      />
      {selectedId && (
        <Modal
          open={previewOpen}
          title={files?.find((f: FileBase) => f.id === selectedId)?.name ?? 'Preview'}
          className={`min-w-[100%] min-h-screen !m-0 !rounded-none`}
          onClose={() => setPreviewOpen(false)}
        >
          <FilePreview id={selectedId} />
        </Modal>
      )}
    </>
  );
};
